/**
 * MTE ForgotPassword form — Phase 4 auth UI structure (M-C2.0+ pre-infra).
 *
 * Form email-only pour déclencher reset. ZERO fetch / ZERO supabase.
 * Após setup infra: wire ao POST /api/auth/forgot-password (D-AUTH-02 v1.2).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Layout, Header } from '../../components/mte/shell';
import '../../styles/auth.css';
import { apiFetch } from '../../lib/auth-fetch';

export const ForgotPasswordSchema = z.object({
  email: z.string().email('Email invalide'),
});

export type ForgotPasswordForm = z.infer<typeof ForgotPasswordSchema>;

interface ForgotPasswordProps {
  onSubmit?: (data: ForgotPasswordForm) => Promise<void> | void;
}

function ForgotPassword(_props: ForgotPasswordProps = {}): JSX.Element {
  const [submitState, setSubmitState] = useState<{ status: 'idle' | 'submitting' | 'sent' | 'error'; message: string }>({ status: 'idle', message: '' });

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const onValid = async (data: ForgotPasswordForm): Promise<void> => {
    setSubmitState({ status: 'submitting', message: '' });
    try {
      await apiFetch<{ resetRequestedAt: string }>('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      setSubmitState({ status: 'sent', message: 'Si un compte existe, un email de réinitialisation a été envoyé.' });
    } catch (e) {
      setSubmitState({ status: 'error', message: e instanceof Error ? e.message : 'Erreur inconnue' });
    }
  };

  return (
    <Layout context="adulte" width="narrow" header={<Header context="adulte" />}>
      <main className="auth-page">
        <div className="auth-card">
          <h1 className="auth-h1">Mot de passe oublié</h1>
          {submitState.status === 'sent' && (
            <div className="auth-message-success" role="status" aria-live="polite" data-testid="forgot-success-region">
              {submitState.message}
            </div>
          )}
          {submitState.status === 'error' && (
            <div className="auth-error-region" role="alert" aria-live="assertive" data-testid="forgot-error-region">
              {submitState.message}
            </div>
          )}
          <form className="auth-form" onSubmit={handleSubmit(onValid)} noValidate data-testid="forgot-form">
            <p className="auth-help-text">
              Renseigne l'email associé à ton compte famille. Tu recevras un lien sécurisé pour choisir un nouveau mot de passe.
            </p>
            <div className="auth-field">
              <label htmlFor="email" className="auth-label">Email</label>
              <input id="email" type="email" autoComplete="email" className="auth-input"
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? 'forgot-email-error' : undefined}
                {...register('email')} />
              {errors.email && <span id="forgot-email-error" className="auth-error" role="alert">{errors.email.message}</span>}
            </div>
            <button type="submit" className="auth-button-primary" disabled={isSubmitting || submitState.status === 'sent'} data-testid="forgot-submit">
              {isSubmitting ? 'Envoi en cours…' : submitState.status === 'sent' ? 'Email envoyé' : 'Recevoir le lien'}
            </button>
            <p className="auth-help-text" style={{ textAlign: 'center' }}>
              <Link to="/login/" className="auth-link">Retour à la connexion</Link>
            </p>
          </form>
        </div>
      </main>
    </Layout>
  );
}

export default ForgotPassword;
